import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Paper,
    Card,
    CardContent,
    Grid,
    Chip
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    Code as CodeIcon
} from '@mui/icons-material';
import axios from 'axios';

const LTIDeepLinkingPage = () => {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadAssignments();
    }, []);

    const loadAssignments = async () => {
        try {
            // Get assignments available for Deep Linking
            // The backend already filtered them based on user permissions
            const response = await axios.get('/client-api/v1/lti/deep-link/assignments/', {
                withCredentials: true
            });
            setAssignments(response.data.assignments || []);
            setLoading(false);
        } catch (err) {
            // Only log in development, not production
            if (process.env.NODE_ENV === 'development') {
                console.error('Failed to load assignments:', err);
            }
            setError(err.response?.data?.error || 'Failed to load assignments');
            setLoading(false);
        }
    };

    const handleSelectAssignment = (assignmentId) => {
        setSelectedId(assignmentId);
    };

    const handleSubmit = async () => {
        if (!selectedId) return;

        setSubmitting(true);
        try {
            const response = await axios.post(
                '/client-api/v1/lti/deep-link/response/',
                {
                    selected_assignments: [selectedId]
                },
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Response will be HTML form that auto-submits back to Moodle
            // Use safer approach: create new document via iframe or window.location
            // Instead of document.write which breaks React

            // Create a blob from HTML response and navigate to it
            const blob = new Blob([response.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);

            // Replace current window location with the blob URL
            // This preserves the response HTML while avoiding document.write()
            window.location.replace(url);

            // Alternative approach if blob doesn't work:
            // Create hidden iframe, write HTML to it, then submit form
            // const iframe = document.createElement('iframe');
            // iframe.style.display = 'none';
            // document.body.appendChild(iframe);
            // const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            // iframeDoc.open();
            // iframeDoc.write(response.data);
            // iframeDoc.close();

        } catch (err) {
            // Only log in development, not production
            if (process.env.NODE_ENV === 'development') {
                console.error('Failed to submit selection:', err);
            }
            setError(err.response?.data?.error || 'Failed to submit selection');
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading assignments...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ mt: 8 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    if (assignments.length === 0) {
        return (
            <Container maxWidth="md" sx={{ mt: 8 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        No Assignments Available
                    </Typography>
                    <Typography color="text.secondary" paragraph>
                        To use DvaKota assignments in your course, you need to:
                    </Typography>
                    <Box component="ol" sx={{ textAlign: 'left', maxWidth: 500, mx: 'auto', mt: 2 }}>
                        <li>Create an account on DvaKota platform</li>
                        <li>Create assignments</li>
                        <li>Link your Moodle account with DvaKota (use the same email)</li>
                    </Box>
                    <Typography color="text.secondary" sx={{ mt: 3 }}>
                        Then return here to select assignments.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon color="primary" />
                    Select Assignment
                </Typography>
                <Typography color="text.secondary" paragraph>
                    Choose an assignment to add to your course
                </Typography>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {assignments.map((assignment) => (
                        <Grid item xs={12} key={assignment.id}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    border: 2,
                                    borderColor: selectedId === assignment.id ? 'primary.main' : 'divider',
                                    bgcolor: selectedId === assignment.id ? 'primary.50' : 'background.paper',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: 'primary.50'
                                    }
                                }}
                                onClick={() => handleSelectAssignment(assignment.id)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                        {selectedId === assignment.id && (
                                            <CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
                                        )}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h6" gutterBottom>
                                                {assignment.title}
                                            </Typography>
                                            {assignment.description && (
                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    {assignment.description}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                {assignment.assignment_type === 'sandbox' ? (
                                                    <Chip
                                                        icon={<CodeIcon />}
                                                        label="Практическое задание"
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<AssignmentIcon />}
                                                        label="Теоретическое задание"
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="contained"
                        size="large"
                        disabled={!selectedId || submitting}
                        onClick={handleSubmit}
                    >
                        {submitting ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Adding to Course...
                            </>
                        ) : (
                            'Add to Course'
                        )}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default LTIDeepLinkingPage;
